import React, { useEffect, useMemo, useState } from 'react';

import { Drawer, Input, Pagination, Typography } from 'antd';
import { useIntl } from 'react-intl';

import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumn } from '@src/components/Table';
import IntlMessages from '@src/util/IntlMessages';

import BoxContainer from '../BoxContainer';
import Title from '../BoxContainer/components/Title';
import FilterContainer from '../FilterContainer';
import MemberAvatar from '../MemberList/MemberAvatar';

import styles from './styles.module.less';
import { IUserListGroup, IUserListMember, IUserListRow } from './interfaces';

const PAGE_SIZE = 25;
const { Text } = Typography;

export interface IProps {
  isVisible: boolean;
  onCancel: () => void;
  users?: IUserListMember[];
  groups?: IUserListGroup[];
  titleId?: string;
  width?: number;
}

const UserListDrawer: React.FC<IProps> = ({
  isVisible,
  onCancel,
  users = [],
  groups = [],
  titleId = 'schedules.list.users.label',
  width = 480,
}) => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const dataSource = useMemo(() => {
    const rows: IUserListRow[] = [];

    users.forEach((user, index) => {
      rows.push({
        key: user.id ? `user-${user.id}` : `user-index-${index}`,
        user,
      });
    });

    groups.forEach((group, groupIndex) => {
      group.users?.forEach((user, userIndex) => {
        const groupKey = group.id ?? group.name ?? groupIndex;
        const userKey = user.id ?? userIndex;

        rows.push({
          key: `group-${groupKey}-${userKey}`,
          user,
          groupName: group.name,
        });
      });
    });

    return rows;
  }, [users, groups]);
  const filteredDataSource = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    if (!term) return dataSource;

    return dataSource.filter(({ groupName, user }) =>
      [
        user.firstName,
        user.lastName,
        user.displayName,
        user.email,
        groupName,
      ].some(value => value?.toLocaleLowerCase().includes(term)),
    );
  }, [dataSource, search]);
  const pageCount = Math.max(1, Math.ceil(filteredDataSource.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const visibleRows = filteredDataSource.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  useEffect(() => {
    if (!isVisible) return;
    setSearch('');
    setPage(1);
  }, [isVisible]);

  const columns = useMemo<ResponsiveTableColumn<IUserListRow>[]>(
    () => [
      {
        key: 'user',
        title: intl.formatMessage({ id: 'users.select.table.user' }),
        dataIndex: 'user',
        align: 'left',
        render: (record: IUserListRow) => (
          <div className="gx-flex-row gx-align-items-center gx-guarnic-gap-1">
            <MemberAvatar size={40} user={record.user} />
            <span>
              {[record.user.firstName, record.user.lastName].filter(Boolean).join(' ') ||
                record.user.displayName ||
                '-'}
            </span>
          </div>
        ),
      },
      {
        key: 'group',
        title: intl.formatMessage({ id: 'users.select.table.group' }),
        dataIndex: 'groupName',
        align: 'left',
        render: (record: IUserListRow) => record.groupName ?? '-',
      },
      {
        key: 'email',
        title: intl.formatMessage({ id: 'users.select.table.email' }),
        dataIndex: 'user',
        align: 'left',
        render: (record: IUserListRow) => record.user.email ?? '-',
      },
    ],
    [intl],
  );

  return (
    <Drawer
      className={styles.userListDrawer}
      destroyOnClose
      open={isVisible}
      onClose={onCancel}
      closable={false}
      footer={null}
      title={null}
      width={width}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            goBack={onCancel}
            title={<Title.Header value={<IntlMessages id={titleId} />} />}
          />
        </BoxContainer>
        <BoxContainer content className={styles.userListBody}>
          <div className={styles.userListSummary}>
            <Text type="secondary">
              {intl.formatMessage(
                { id: 'general.showing.results' },
                { filtered: filteredDataSource.length, total: dataSource.length },
              )}
            </Text>
          </div>
          <Input.Search
            allowClear
            aria-label={intl.formatMessage({ id: 'users.selectionDrawer.searchPlaceholder' })}
            className={styles.userListSearch}
            placeholder={intl.formatMessage({ id: 'users.selectionDrawer.searchPlaceholder' })}
            value={search}
            onChange={event => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
          <div className={styles.userListTableWrapper}>
            <ResponsiveTable<IUserListRow>
              type="light"
              dataSource={visibleRows}
              columns={columns}
              className={styles.userListTable}
              maxHeight="calc(100dvh - 300px)"
              stickyHeader
              wrapperClassName={styles.userListTableScroll}
            />
          </div>
          {filteredDataSource.length > PAGE_SIZE && (
            <div className={styles.userListPagination}>
              <Pagination
                aria-label={intl.formatMessage({ id: 'general.pagination.label' })}
                current={currentPage}
                hideOnSinglePage
                pageSize={PAGE_SIZE}
                responsive
                showLessItems
                showSizeChanger={false}
                showTotal={(total, [from, to]) =>
                  intl.formatMessage(
                    { id: 'users.selectionDrawer.paginationTotal' },
                    { start: from, end: to, total },
                  )}
                size="small"
                total={filteredDataSource.length}
                onChange={setPage}
              />
            </div>
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default UserListDrawer;
